import React, { MouseEvent, ReactElement, useState } from 'react';
import styled from 'styled-components';
import {
  Drawer,
  List,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
} from '@mui/material';
import { useLocation } from 'react-router-dom';

import { ThemeContext } from '@tiui/remote.gfi.providers.theme';

type Item = {
  title: string;
  icon: ReactElement;
};

export type SidebarProps = {
  /**
   * @description Menu items to be rendered in the sidebar component.
   * @example
   * items: [
   *  { title: 'Accounts', icon: <AppsIcon /> },
   * { title: 'GFI Products', icon: <AppsIcon /> },
   * ]
   */
  items: Item[];

  /**
   * @description The variant to use.
   * @default 'permanent'
   */
  variant?: 'permanent' | 'persistent' | 'temporary';

  /**
   * @description Side from which the drawer will appear.
   * @default 'left'
   */
  anchor?: 'left' | 'top' | 'right' | 'bottom';

  /**
   * @description If true, the component is shown.
   * @default true
   */
  open?: boolean;

  /**
   * @description initially selected sidebar item, -1 implies no item selected
   * @default 0
   */
  initialSelectedIndex?: number;

  /**
   * @description OnCLick Listener for list items.
   * @param {MouseEvent} event
   * @param {number} index
   */
  onClick?: (event: MouseEvent<HTMLElement>, index: number) => void;
};

const drawerWidth = 240;

const Container = styled.div`
  display: flex;

  svg.MuiSvgIcon-root {
    color: ${(props) => props?.itemColor};
  }

  div.MuiListItemIcon-root {
    min-width: 32px;
  }

  span.MuiTypography-root {
    color: ${(props) => props?.itemColor};
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    line-height: 150%;
    letter-spacing: 0.15px;
  }

  div.MuiListItemButton-root {
    padding: 14px 28px;
  }

  div.MuiDrawer-root > div.MuiPaper-root {
    border-right: 1px solid rgba(0, 0, 0, 0.08);
  }
`;

const locationMap = {
  '/accounts': 0,
  '/products': 1,
};

export function Sidebar({
  items = [] as Item[],
  variant = 'permanent',
  anchor = 'left',
  open = true,
  initialSelectedIndex = 0,
  onClick = (event, index) => {
    console.log(`Sidebar item ${index} clicked`);
  },
}: SidebarProps) {
  const theme = React.useContext(ThemeContext);
  const [selectedIndex, setSelectedIndex] = useState(initialSelectedIndex);
  const location = useLocation();

  React.useEffect(() => {
    setSelectedIndex(locationMap[location.pathname] ?? -1);
  }, [location.pathname]);

  const handleListItemClick = (
    event: MouseEvent<HTMLElement>,
    index: number
  ) => {
    setSelectedIndex(index);
    onClick(event, index);
  };

  return (
    <Container itemColor={theme?.primaryExtraDark}>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: theme.backgroundSecondary,
          },
        }}
        variant={variant}
        anchor={anchor}
        open={open}
      >
        <Toolbar />
        <Divider />
        <List component="nav" sx={{ pt: 3 }}>
          {items.map((item: Item, index: number) => (
            <ListItemButton
              key={item.title}
              selected={selectedIndex === index}
              onClick={(event) => handleListItemClick(event, index)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.title} />
            </ListItemButton>
          ))}
        </List>
        <Divider />
      </Drawer>
    </Container>
  );
}
